// src/components/dashboard/rent_to_own/rentToOwnFormat.js
// FT-173 (финальное ревью, H6): формат денег, процентов и дат модуля
// «Аренда с выкупом» — одно место на все экраны модуля и панель в карточке
// бронирования.
//
// Общего форматтера денег в приложении нет: BookingCard клеит сумму и символ
// валюты шаблоном (`{{ sum }}{{ currency }}`), остальные экраны держат свои
// локальные formatAmount. Отсюда взяты два соглашения приложения:
//   - символ валюты — `getCompany.company.currency` (как computed currency
//     в BookingCard.vue), колонка `companies.currency`, по умолчанию «₽»;
//   - разделитель тысяч — Intl.NumberFormat по языку интерфейса (как
//     moneyConfirmGuard.js и CrmLeadContactSection.vue), а не жёстко ru-RU.
// Даты — `DD-MM-YYYY`, формат карточки бронирования.
//
// API модуля отдаёт деньги строкой с фиксированной точностью
// (ContractPresenter.money: "20000.00"), ставку — с тремя знаками
// ("12.500"), даты — ISO (`2026-10-24`, события — с миллисекундами).
import moment from "moment";

export const EMPTY = "—";

// Деньги в копейках целым числом — остаток и прощаемая сумма считаются без
// потерь на float-вычитании.
export function moneyToCents(value) {
  if (value == null) return 0;
  const str = String(value).trim();
  const negative = str.startsWith("-");
  const unsigned = negative ? str.slice(1) : str;
  const [intPart, fracPart = ""] = unsigned.split(".");
  const frac = (fracPart + "00").slice(0, 2);
  const cents = (parseInt(intPart || "0", 10) || 0) * 100 + (parseInt(frac || "0", 10) || 0);
  return negative ? -cents : cents;
}

export function centsToMoney(cents) {
  const negative = cents < 0;
  const abs = Math.abs(cents);
  const intPart = Math.floor(abs / 100);
  const frac = String(abs % 100).padStart(2, "0");
  return `${negative ? "-" : ""}${intPart}.${frac}`;
}

function numberFormat(locale, options) {
  try {
    return new Intl.NumberFormat(locale || undefined, options);
  } catch (e) {
    return new Intl.NumberFormat(undefined, options);
  }
}

// "20000.00" → "20 000 ₽"; копейки показываются, только если они есть
// ("3800.50" → "3 800,50 ₽").
export function formatMoney(value, { currency = "", locale } = {}) {
  if (value == null || value === "") return EMPTY;
  const cents = moneyToCents(value);
  const whole = cents % 100 === 0;
  const text = numberFormat(locale, {
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: whole ? 0 : 2,
  }).format(cents / 100);
  return currency ? `${text} ${currency}` : text;
}

// Ставка и прогресс — без хвостовых нулей: "12.500" → "12,5%", "0.000" → "0%".
export function formatPercent(value, { locale } = {}) {
  if (value == null || value === "") return EMPTY;
  const number = Number(value);
  if (!Number.isFinite(number)) return EMPTY;
  return `${numberFormat(locale, { maximumFractionDigits: 3 }).format(number)}%`;
}

// "2026-10-24" → "24-10-2026".
export function formatDate(value) {
  if (!value) return EMPTY;
  const parsed = moment(String(value).slice(0, 10), "YYYY-MM-DD", true);
  return parsed.isValid() ? parsed.format("DD-MM-YYYY") : String(value);
}

// "2026-09-25T10:11:12.345+03:00" → "25-09-2026 10:11" (местное время, без
// секунд и миллисекунд).
export function formatDateTime(value) {
  if (!value) return EMPTY;
  const parsed = moment(value, moment.ISO_8601, true);
  return parsed.isValid() ? parsed.format("DD-MM-YYYY HH:mm") : String(value);
}
